import {
  useEffect,
  useMemo,
  useState,
  useRef,
  MouseEvent,
  useCallback,
} from "react";
import { DateRangePicker, StaticRange } from "react-date-range";
import clsx from "clsx";
import { useMediaQuery } from "react-responsive";
import { compareAsc, format } from "date-fns";

import { QUERY_WIDTHS, getDatePickerButton } from "@utils/index";
import { useCommonStore } from "@stores/index";
import { useClickOutside, useNotify } from "@hooks/index";
import { useUserGetPeriods } from "@providers/index";
import {
  CalendarMonthSvg,
  ChevronDownSvg,
  TrashXSvg,
  FormInput,
} from "@components/index";

// .replace(/-/g, "/") - needs for Safari
const formatParamDate = (date: string) =>
  date
    ? new Date(
        `${date.split("-")[0]}-${parseInt(date.split("-")[1], 10)}-${date.split("-")[2]}`.replace(
          /-/g,
          "/",
        ),
      )
    : new Date();

const formatInputDate = (date: string) =>
  date
    ? new Date(
        `${date.split(".")[2]}-${parseInt(date.split(".")[1], 10)}-${date.split(".")[0]}`.replace(
          /-/g,
          "/",
        ),
      )
    : new Date();

const formatStateDate = (date: Date) => {
  if (!date) {
    return "";
  }

  let day = String(date.getDate());
  day = day.length === 1 ? `0${day}` : day;
  let month = String(date.getMonth() + 1);
  month = month.length === 1 ? `0${month}` : month;

  return `${day}.${month}.${date.getFullYear()}`;
};

const formatEnteredDate = (date: string) => {
  const justNumbers = date.replace(/[^0-9]/g, "");
  let str = justNumbers.slice(0, 2);
  str += justNumbers.length > 2 ? "." : "";
  str += justNumbers.slice(2, 4);
  str += justNumbers.length > 4 ? "." : "";
  str += justNumbers.slice(4, 8);

  return str;
};

export const RangePicker = ({
  parent = "",
  showPeriods = true,
  startDate,
  endDate,
  prefix = "",
  disabled,
  disableClear = false,
  shouldCloseOnClickOutside = false,
  variant = "filter",
  error,
  onSetStartDate,
  onSetEndDate,
  minDate,
  maxDate,
  calendarContainerClassName,
  dataTestid,
}: {
  parent?: string;
  showPeriods?: boolean;
  startDate: string;
  endDate: string;
  prefix?: string;
  disabled?: boolean;
  disableClear?: boolean;
  shouldCloseOnClickOutside?: boolean;
  variant?: "filter" | "formField";
  error?: string;
  onSetStartDate: (startDate: string) => void;
  onSetEndDate: (endDate: string) => void;
  minDate?: Date;
  maxDate?: Date;
  calendarContainerClassName?: string;
  dataTestid?: string;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();
  const setCommonState = useCommonStore(({ setState }) => setState);

  const [isShow, setIsShow] = useState(false);

  const [staticRanges, setStaticRanges] = useState<StaticRange[]>([]);

  const rangePickerRef = useRef(null);
  useClickOutside(
    rangePickerRef,
    () => shouldCloseOnClickOutside && setIsShow(false),
  );

  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [startDateInputElement, setStartDateInputElement] =
    useState<HTMLInputElement>();
  const [endDateInputElement, setEndDateInputElement] =
    useState<HTMLInputElement>();
  const [startDateChangedValue, setStartDateChangedValue] = useState<Date>();
  const [endDateChangedValue, setEndDateChangedValue] = useState<Date>();

  const buttonLabel = useMemo(() => {
    const title = startDate || endDate ? "Selected:" : "Select";
    const start = !startDate
      ? ""
      : `${startDate.split("-")[2]}/${startDate.split("-")[1]}/${startDate.split("-")[0]}`;
    const end = !endDate
      ? ""
      : `${endDate.split("-")[2]}/${endDate.split("-")[1]}/${endDate.split("-")[0]}`;
    return (
      <>
        <span className="text-black-54">{title}</span>
        <span className="font-bold">
          {!!start && !!end ? `${start} - ${end}` : ""}
        </span>
      </>
    );
  }, [startDate, endDate]);

  const inputLabel = useMemo(() => {
    const start = !startDate
      ? ""
      : `${startDate.split("-")[2]}/${startDate.split("-")[1]}/${startDate.split("-")[0]}`;
    const end = !endDate
      ? ""
      : `${endDate.split("-")[2]}/${endDate.split("-")[1]}/${endDate.split("-")[0]}`;
    return !!start && !!end ? `${start} - ${end}` : "";
  }, [startDate, endDate]);

  useEffect(() => {
    if (
      !document.querySelector(
        `${parent ? parent + " " : ""}.rdrDateRangePickerWrapper .rdrDateRangeWrapper .rdrDateRangeFooter`,
      )
    ) {
      const btnApply = getDatePickerButton("Apply", "apply", "apply");
      const footer = document.createElement("div");
      footer.className = "rdrDateRangeFooter";
      if (!disableClear) {
        const btnClear = getDatePickerButton("Clear all", "clear", "clearAll");
        btnClear.onclick = () => {
          onSetStartDate("");
          onSetEndDate("");
          setState([
            {
              startDate: new Date(),
              endDate: new Date(),
              key: "selection",
            },
          ]);
          setIsShow(false);
          const invalidDateSection = document.getElementsByClassName(
            "rdrInvalidDateSection",
          )?.[0];
          if (invalidDateSection) {
            invalidDateSection.classList.add("hidden");
          }
        };
        footer.appendChild(btnClear);
      }
      footer.appendChild(btnApply);
      const pickerBottom = document.querySelector(
        `${parent ? parent + " " : ""}.rdrDateRangePickerWrapper .rdrDateRangeWrapper`,
      );
      pickerBottom?.appendChild(footer);
    }
  }, [onSetStartDate, onSetEndDate, parent, disableClear]);

  const handleInput = useCallback(
    ({ currentTarget }: Event) => {
      if (currentTarget) {
        const invalidDateSection = document.querySelector(
          `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
        );
        invalidDateSection?.classList.add("hidden");
        const value = (currentTarget as HTMLInputElement).value;
        const formattedValue = formatEnteredDate(value);
        (currentTarget as HTMLInputElement).value = formattedValue;
        const convertedToDate = formatInputDate(value);
        if (
          value.length < 10 ||
          convertedToDate.toString() === "Invalid Date"
        ) {
          invalidDateSection?.classList.remove("hidden");
        }
      }
    },
    [parent],
  );

  useEffect(() => {
    if (
      startDateChangedValue &&
      compareAsc(startDateChangedValue, state[0].startDate) !== 0
    ) {
      const isStartDateAfterEndDate =
        compareAsc(startDateChangedValue, state[0].endDate) > 0;
      setState([
        {
          ...state[0],
          startDate: startDateChangedValue,
          endDate: isStartDateAfterEndDate
            ? startDateChangedValue
            : state[0].endDate,
        },
      ]);
      setStartDateChangedValue(undefined);
    }
  }, [startDateChangedValue, state, parent]);

  useEffect(() => {
    if (
      endDateChangedValue &&
      compareAsc(endDateChangedValue, state[0].endDate) !== 0
    ) {
      const isEndDateBeforeStartDate =
        compareAsc(endDateChangedValue, state[0].startDate) < 0;
      setState([
        {
          ...state[0],
          startDate: isEndDateBeforeStartDate
            ? endDateChangedValue
            : state[0].startDate,
          endDate: endDateChangedValue,
        },
      ]);
      setEndDateChangedValue(undefined);
    }
  }, [endDateChangedValue, state, parent]);

  const handleChange = useCallback(
    ({ currentTarget }: Event, property: "startDate" | "endDate") => {
      const date = formatInputDate((currentTarget as HTMLInputElement).value);
      const isValidDate = !date.toString().toLowerCase().includes("invalid");
      if (property === "startDate" && isValidDate) {
        setStartDateChangedValue(date);
      } else if (property === "endDate" && isValidDate) {
        setEndDateChangedValue(date);
      }
    },
    [],
  );

  useEffect(() => {
    if (
      !document.querySelector(
        `${parent ? parent + " " : ""}.rdrDateRangePickerWrapper .rdrDateRangeWrapper .rdrDateDisplayWrapper .rdrInputsSection`,
      )
    ) {
      const inputStartDate = document.createElement("input");
      const inputEndDate = document.createElement("input");
      inputStartDate.name = "startDate";
      inputStartDate.maxLength = 10;
      inputStartDate.placeholder = "Start date";
      inputStartDate.className =
        "w-[142px] md:w-[310px] h-[48px] px-4 border border-black-16 hover:border-black-32 focus:border-black-32 rounded-lg text-200 text-black-100 placeholder:text-black-54 outline-none";
      inputEndDate.name = "endDate";
      inputEndDate.maxLength = 10;
      inputEndDate.placeholder = "End date";
      inputEndDate.className =
        "w-[142px] md:w-[310px] h-[48px] px-4 border border-black-16 hover:border-black-32 focus:border-black-32 rounded-lg text-200 text-black-100 placeholder:text-black-54 outline-none";
      const inputsSection = document.createElement("div");
      inputsSection.className =
        "rdrInputsSection flex justify-between px-4 pt-2";
      inputsSection.appendChild(inputStartDate);
      inputsSection.appendChild(inputEndDate);
      const pickerTop = document.querySelector(
        `${parent ? parent + " " : ""}.rdrDateRangePickerWrapper .rdrDateRangeWrapper .rdrDateDisplayWrapper`,
      );
      pickerTop?.appendChild(inputsSection);

      const invalidDateSection = document.createElement("div");
      invalidDateSection.className =
        "rdrInvalidDateSection text-center text-red-100 hidden";
      invalidDateSection.innerText = "You entered an invalid date";
      pickerTop?.appendChild(invalidDateSection);

      inputStartDate.onblur = handleInput;
      inputStartDate.onchange = (event) => handleChange(event, "startDate");
      setStartDateInputElement(inputStartDate);

      inputEndDate.onblur = handleInput;
      inputEndDate.onchange = (event) => handleChange(event, "endDate");
      setEndDateInputElement(inputEndDate);
    }
  }, [handleChange, handleInput, parent]);

  useEffect(() => {
    if (startDateInputElement) {
      startDateInputElement.value = formatStateDate(state[0].startDate);
    }
    if (endDateInputElement) {
      endDateInputElement.value = formatStateDate(state[0].endDate);
    }
    const invalidDateSection = document.querySelector(
      `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
    );
    invalidDateSection?.classList.add("hidden");
  }, [startDateInputElement, endDateInputElement, state, parent]);

  useEffect(() => {
    const btn: HTMLButtonElement | null = document.querySelector(
      `${parent ? parent + " " : ""}.rdrDateRangePickerWrapper .rdrDateRangeWrapper .rdrDateRangeFooter button.apply`,
    );
    if (btn) {
      btn.onclick = () => {
        const invalidDateSection = document.querySelector(
          `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
        );
        if (
          invalidDateSection &&
          !invalidDateSection.classList.contains("hidden")
        ) {
          return;
        }

        if (
          invalidDateSection &&
          ((minDate &&
            compareAsc(
              new Date(format(state[0].startDate, "yyyy-MM-dd")),
              new Date(format(minDate, "yyyy-MM-dd")),
            ) < 0) ||
            (maxDate &&
              compareAsc(
                new Date(format(state[0].endDate, "yyyy-MM-dd")),
                new Date(format(maxDate, "yyyy-MM-dd")),
              ) > 0))
        ) {
          invalidDateSection.classList.remove("hidden");
          return;
        }

        const startDate = state[0].startDate;
        const endDate = state[0].endDate;
        if (startDate && endDate) {
          const sDateMonth = startDate.getMonth() + 1;
          const sDateMonthStr =
            sDateMonth < 10 ? `0${sDateMonth}` : `${sDateMonth}`;
          const sDateDay = startDate.getDate();
          const sDateDayStr = sDateDay < 10 ? `0${sDateDay}` : `${sDateDay}`;
          onSetStartDate(
            `${startDate.getFullYear()}-${sDateMonthStr}-${sDateDayStr}`,
          );
          const eDateMonth = endDate.getMonth() + 1;
          const eDateMonthStr =
            eDateMonth < 10 ? `0${eDateMonth}` : `${eDateMonth}`;
          const eDateDay = endDate.getDate();
          const eDateDayStr = eDateDay < 10 ? `0${eDateDay}` : `${eDateDay}`;
          onSetEndDate(
            `${endDate.getFullYear()}-${eDateMonthStr}-${eDateDayStr}`,
          );
        }
        setIsShow(false);
      };
    }
  }, [state, onSetStartDate, onSetEndDate, parent, minDate, maxDate]);

  useEffect(() => {
    setState([
      {
        startDate: formatParamDate(startDate),
        endDate: formatParamDate(endDate),
        key: "selection",
      },
    ]);
  }, [startDate, endDate]);

  const {
    isPending: isPendingGetPeriods,
    isSuccess: isSuccessGetPeriods,
    data: dataGetPeriods,
    isError: isErrorGetPeriods,
    error: errorGetPeriods,
  } = useUserGetPeriods({ enabled: showPeriods });

  useEffect(() => {
    if (isSuccessGetPeriods && dataGetPeriods) {
      const periods: StaticRange[] = dataGetPeriods.periods.map((period) => ({
        label: `
          ${period.title}, (${format(new Date(period.startDate), "dd.MM.yyyy")}-${format(new Date(period.endDate), "dd.MM.yyyy")})
        `,
        range: () => ({
          startDate: new Date(period.startDate),
          endDate: new Date(period.endDate),
        }),
        isSelected: () => true,
      }));
      setStaticRanges(periods);
    }
  }, [isSuccessGetPeriods, dataGetPeriods]);

  useEffect(() => {
    if (isErrorGetPeriods && errorGetPeriods) {
      notify("error", errorGetPeriods.message);
    }
  }, [isErrorGetPeriods, errorGetPeriods, notify]);

  useEffect(() => {
    setCommonState({ isLoading: isPendingGetPeriods });
  }, [isPendingGetPeriods, setCommonState]);

  const handleShow = () => {
    setIsShow(!isShow);
    const invalidDateSection = document.getElementsByClassName(
      "rdrInvalidDateSection",
    )?.[0];
    if (invalidDateSection) {
      invalidDateSection.classList.add("hidden");
    }
  };

  const handleClearDropdownValue = (event: MouseEvent) => {
    event.stopPropagation();
    onSetStartDate("");
    onSetEndDate("");
    const invalidDateSection = document.getElementsByClassName(
      "rdrInvalidDateSection",
    )?.[0];
    if (invalidDateSection) {
      invalidDateSection.classList.add("hidden");
    }
  };

  return (
    <div
      ref={rangePickerRef}
      className={clsx(
        variant === "filter" && "w-full md:w-fit",
        disabled && "pointer-events-none opacity-50",
      )}
      data-testid={dataTestid}
    >
      {variant === "filter" ? (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-x-2 rounded-lg bg-white-100 p-3 text-primary-300 md:w-fit"
          onClick={handleShow}
        >
          <div className="flex items-center gap-x-2">
            <CalendarMonthSvg />
            {buttonLabel}
          </div>
          {(startDate || endDate) && !disableClear ? (
            <div className="text-black-100" onClick={handleClearDropdownValue}>
              <TrashXSvg />
            </div>
          ) : (
            <div className="*:size-[12px] *:stroke-[3px]">
              <ChevronDownSvg />
            </div>
          )}
        </button>
      ) : (
        <FormInput
          name={`${prefix ? `${prefix}__` : ""}dates`}
          label="Date range"
          icon={<CalendarMonthSvg />}
          value={inputLabel}
          readOnly
          error={error}
          onClick={() => setIsShow(true)}
        />
      )}
      <DateRangePicker
        className={clsx(
          "before:content-[' '] absolute right-10 z-10 mt-3 rounded-lg border border-lightGray shadow-md before:absolute before:-top-1 before:right-4 before:z-0 before:block before:h-3 before:w-3 before:rotate-45 before:bg-white-100",
          !isShow && "!hidden",
          !showPeriods &&
            "[&>.rdrCalendarWrapper]:rounded-lg [&>.rdrDefinedRangesWrapper]:hidden",
          calendarContainerClassName,
        )}
        months={isMobile ? 1 : 2}
        weekStartsOn={1}
        direction="horizontal"
        moveRangeOnFirstSelection={false}
        ranges={state}
        onChange={(range) =>
          setState([
            {
              ...range.selection,
              startDate: range.selection.startDate || new Date(),
              endDate: range.selection.endDate || new Date(),
              key: "selection",
            },
          ])
        }
        inputRanges={[]}
        renderStaticRangeLabel={({ label }) => label}
        staticRanges={showPeriods ? staticRanges : []}
        minDate={minDate}
        maxDate={maxDate}
      />
    </div>
  );
};
